import React from "react";
import { Link } from "react-router-dom";

import Card from "../../ui/Card";
import Badge from "../../ui/Badge";
import { getScoreBadgeClass } from "../../../utils/scoreColors";

const btnSecondary =
  "inline-flex items-center justify-center gap-2 rounded tracking-wide uppercase text-xs transition-all duration-200 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 focus-visible:ring-offset-theme-main bg-theme-panel text-theme-text border border-theme-border font-mono hover:border-theme-border-bright focus-visible:ring-theme-accent px-3 py-2";

function dash(val) {
  return val != null && String(val).trim() !== "" ? val : "—";
}

export default function AuditSalesProspects({ prospects, taskStatus }) {
  const rows = prospects || [];
  const stillRunning = taskStatus === "running" || taskStatus === "partially_completed";

  return (
    <Card className="p-4 md:p-5 overflow-hidden">
      <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-3">
        <div>
          <h3 className="font-display font-bold italic text-base text-theme-on-background mb-1">
            Best-fit opportunities
          </h3>
          <p className="font-mono text-[10px] text-theme-dim leading-snug">
            Companies worth acting on, ranked by fit and summarized from website analysis.
          </p>
        </div>
        <Link to="/leads/" className={btnSecondary}>
          View all leads
        </Link>
      </div>
      {stillRunning ? (
        <p className="font-mono text-[11px] text-theme-dim mb-3">Updates as profiles load.</p>
      ) : null}
      {!rows.length ? (
        <p className="text-theme-muted text-sm">No matched companies in queue for this run yet.</p>
      ) : (
        <div className="overflow-x-auto rounded-lg border border-theme-border -mx-1">
          <table className="w-full text-left text-sm">
            <thead className="bg-theme-panel/80 border-b border-theme-border font-mono text-[10px] uppercase tracking-[0.08em] text-theme-dim">
              <tr>
                <th className="px-3 py-2 min-w-[10rem]">Company</th>
                <th className="px-3 py-2 text-right whitespace-nowrap">Fit</th>
                <th className="px-3 py-2 min-w-[14rem]">From website analysis</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row) => {
                const profileBits = [row.business_model, row.offer_category, row.company_maturity]
                  .filter(Boolean)
                  .join(" · ");
                const fitBrand = row.best_fit_brand ?? "Brand";
                const fitPct = row.best_fit_score ?? row.avg_fit_score;
                const title = row.name;
                const companyInner = row.top_lead_hash ? (
                  <Link
                    to={`/leads/${row.top_lead_hash}/`}
                    className="font-medium text-theme-primary hover:opacity-90"
                  >
                    {title}
                  </Link>
                ) : (
                  <span className="font-medium text-theme-on-background">{title}</span>
                );
                const industrySub = row.industry ? (
                  <div className="font-mono text-[11px] text-theme-muted mt-0.5 leading-snug line-clamp-2">
                    {row.industry}
                  </div>
                ) : null;
                return (
                  <tr key={row.company_hash} className="border-b border-theme-border last:border-b-0 hover:bg-theme-panel/50">
                    <td className="px-3 py-2 align-top">
                      <div className="min-w-0">
                        <div>
                          {companyInner}
                          {row.lead_count > 1 ? (
                            <span className="ml-1.5 font-mono text-[10px] text-theme-muted">
                              ({row.lead_count} leads)
                            </span>
                          ) : null}
                        </div>
                        {industrySub}
                      </div>
                    </td>
                    <td className="px-3 py-2 align-top text-right whitespace-nowrap">
                      <Badge variant="inherit" className={getScoreBadgeClass(fitPct)}>
                        {fitBrand} - {fitPct}%
                      </Badge>
                    </td>
                    <td className="px-3 py-2 align-top text-theme-text text-xs max-w-md">
                      <span className="line-clamp-2">{dash(row.summary)}</span>
                      {profileBits ? (
                        <div className="font-mono text-[10px] text-theme-muted mt-1 line-clamp-1">
                          {profileBits}
                        </div>
                      ) : null}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </Card>
  );
}
